import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getBaseURL } from "../../services/api";
import { ToastProvider, useToast } from "./AdminToast";
import { S, COLORS, GLOBAL_ADMIN_CSS } from "./adminStyles";

const API_URL = getBaseURL();

const ManageDepositsInner = () => {
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("adminToken");
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    if (!token) navigate("/winrox11-super-admin");
    else fetchPendingDeposits();
  }, [token, navigate]);

  const fetchPendingDeposits = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/deposits/pending`, { headers: { Authorization: `Bearer ${token}` } });
      setDeposits(res.data);
    } catch (err) {
      toast.error("Failed to load pending deposits.");
    } finally { setLoading(false); }
  };

  const handleApprove = async (txId, amount, name) => {
    if (!window.confirm(`Approve ₹${amount} for ${name}?\n\nVerify the UTR in your bank before approving.`)) return;
    try {
      await axios.post(`${API_URL}/admin/deposits/approve/${txId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`₹${amount} deposit approved for ${name}!`);
      fetchPendingDeposits();
    } catch (err) {
      toast.error("Approval failed: " + (err.response?.data?.error || err.message));
    }
  };

  const handleReject = async (txId, amount, name) => {
    if (!window.confirm(`Reject ₹${amount} for ${name}? Hold balance will be cleared.`)) return;
    try {
      await axios.post(`${API_URL}/admin/deposits/reject/${txId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.warn(`₹${amount} deposit rejected for ${name}.`);
      fetchPendingDeposits();
    } catch (err) {
      toast.error("Rejection failed: " + (err.response?.data?.error || err.message));
    }
  };

  const totalPending = deposits.reduce((s, d) => s + Number(d.amount || 0), 0);

  return (
    <div style={S.page}>
      <style>{GLOBAL_ADMIN_CSS}</style>
      <button onClick={() => navigate("/winrox11-super-admin/dashboard")} style={S.backBtn}>← Back to Dashboard</button>

      <div style={S.topBar}>
        <div>
          <div style={S.pageTitle}>💰 Pending Deposits</div>
          <div style={S.pageSubtitle}>Manual UPI Approval Queue</div>
        </div>
        <div style={{ display: "flex", gap: "12px" }}>
          <div style={{ background: COLORS.card, border: `1px solid ${COLORS.amber}30`, borderRadius: "12px", padding: "12px 18px", textAlign: "center" }}>
            <div style={{ fontSize: "22px", fontWeight: 800, color: COLORS.amber }}>{deposits.length}</div>
            <div style={{ fontSize: "10px", color: COLORS.textMuted, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase" }}>Pending</div>
          </div>
          <div style={{ background: COLORS.card, border: `1px solid ${COLORS.green}30`, borderRadius: "12px", padding: "12px 18px", textAlign: "center" }}>
            <div style={{ fontSize: "22px", fontWeight: 800, color: COLORS.green }}>₹{totalPending.toLocaleString("en-IN")}</div>
            <div style={{ fontSize: "10px", color: COLORS.textMuted, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase" }}>Total Amount</div>
          </div>
        </div>
      </div>

      {/* Warning tip */}
      <div style={{ background: COLORS.amber + "10", border: `1px solid ${COLORS.amber}30`, borderRadius: "12px", padding: "14px 18px", marginBottom: "24px", display: "flex", alignItems: "flex-start", gap: "10px" }}>
        <span style={{ fontSize: "18px" }}>⚠️</span>
        <div>
          <div style={{ fontSize: "12px", fontWeight: 700, color: COLORS.amber, marginBottom: "4px" }}>Always verify UTR before approving</div>
          <div style={{ fontSize: "12px", color: COLORS.textMuted }}>Cross-check the UTR number and amount in your bank/UPI app before clicking Approve. Approved transactions immediately credit the user's wallet.</div>
        </div>
      </div>

      {loading ? (
        <div style={S.loading}>Loading pending deposits...</div>
      ) : deposits.length === 0 ? (
        <div style={{ ...S.empty, fontSize: "16px" }}>
          <div style={{ fontSize: "48px", marginBottom: "12px" }}>😴</div>
          No pending deposit requests found.
        </div>
      ) : (
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: "16px", overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "700px" }}>
              <thead>
                <tr style={{ background: COLORS.inputBg }}>
                  {["User", "Amount", "UTR Number", "Date", "Actions"].map(h => (
                    <th key={h} style={{
                      padding: "14px 16px",
                      fontSize: "10px",
                      fontWeight: 700,
                      color: COLORS.textMuted,
                      letterSpacing: "1.5px",
                      textTransform: "uppercase",
                      textAlign: h === "Actions" ? "center" : "left",
                      borderBottom: `1px solid ${COLORS.cardBorder}`,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {deposits.map((d, idx) => (
                  <tr key={d.tx_id} className="admin-row-hover" style={{ borderBottom: `1px solid ${COLORS.cardBorder}`, background: idx % 2 === 0 ? COLORS.card : COLORS.surface, transition: "background 0.1s" }}>
                    <td style={{ padding: "16px" }}>
                      <div style={{ fontWeight: 700, fontSize: "14px", color: COLORS.text }}>{d.first_name} {d.last_name}</div>
                      <div style={{ fontSize: "11px", color: COLORS.textMuted, marginTop: "3px" }}>ID: {d.user_id} · {d.mobile}</div>
                    </td>
                    <td style={{ padding: "16px" }}>
                      <div style={{ fontSize: "20px", fontWeight: 800, color: COLORS.green }}>₹{Number(d.amount).toLocaleString("en-IN")}</div>
                    </td>
                    <td style={{ padding: "16px" }}>
                      <code style={{ background: COLORS.indigo + "15", border: `1px solid ${COLORS.indigo}30`, padding: "5px 10px", borderRadius: "6px", color: COLORS.indigoLight, fontSize: "13px", fontWeight: 700 }}>
                        {d.utr || "—"}
                      </code>
                    </td>
                    <td style={{ padding: "16px", fontSize: "12px", color: COLORS.textMuted }}>
                      {new Date(d.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </td>
                    <td style={{ padding: "16px" }}>
                      <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                        <button
                          className="admin-btn-hover"
                          onClick={() => handleApprove(d.tx_id, d.amount, d.first_name)}
                          style={{ padding: "8px 16px", background: COLORS.green + "20", color: COLORS.green, border: `1px solid ${COLORS.green}40`, borderRadius: "8px", cursor: "pointer", fontSize: "12px", fontWeight: 700, transition: "all 0.15s" }}
                        >
                          ✅ Approve
                        </button>
                        <button
                          className="admin-btn-hover"
                          onClick={() => handleReject(d.tx_id, d.amount, d.first_name)}
                          style={{ padding: "8px 16px", background: COLORS.red + "15", color: COLORS.red, border: `1px solid ${COLORS.red}30`, borderRadius: "8px", cursor: "pointer", fontSize: "12px", fontWeight: 700, transition: "all 0.15s" }}
                        >
                          ❌ Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const ManageDeposits = () => (
  <ToastProvider>
    <ManageDepositsInner />
  </ToastProvider>
);

export default ManageDeposits;
